/**
 * @fileoverview Connect provider billing reports to shared spending detail presentation. Organization billed totals, token usage, and local workflow attribution retain their own scope and availability. `AiSpendDashboard.js` can supply an already-loaded report to ProviderSpendDetails without another fetch or filter set. Provider-specific wrappers own endpoint selection; rendering never reconciles invoices or estimates missing amounts as free usage.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import api from '../../api'
import { AppButton } from '../shared'
import { AdminEmptyState, AdminNote, AdminPage, AdminSection } from './AdminChrome'
import './OpenAISpendDashboard.css'

const PERIOD_OPTIONS = [7, 14, 30, 60, 90]



function ProviderSpendDashboard({
    embedded = false,
    providerName,
    endpoint,
    title,
    description,
    unconfiguredKeyName,
    dailyDescription,
    lineItemsDescription,
    sourceDescription,
}) {
    const [days, setDays] = useState(30)
    const [dashboard, setDashboard] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const loadDashboard = useCallback(

    async () => {
        setLoading(true)
        setError('')
        try {
            const response = await api.get(endpoint, { params: { days } })
            setDashboard(response.data)
        } catch (requestError) {
            console.error(`Failed to load ${providerName} spend dashboard:`, requestError)
            setError(requestError.response?.data?.error || `${providerName} spend dashboard failed to load.`)
        } finally {
            setLoading(false)
        }
    }, [days, endpoint, providerName])

    useEffect(() => {
        loadDashboard()
    }, [loadDashboard])

    const costs = dashboard?.costs || {}
    const usage = dashboard?.usage || null
    const localActivity = dashboard?.local_activity || {}
    const currency = costs.currency || 'usd'
    const dailyRows = useMemo(
        () => mergeDailyRows(costs.daily || [], localActivity.by_day || []),
        [costs.daily, localActivity.by_day]
    )
    const maxDailyAmount = useMemo(
        () => Math.max(0, ...dailyRows.map((row) => Number(row.billed_amount || 0))),
        [dailyRows]
    )
    const Wrapper = embedded ? AdminSection : AdminPage
    const providerSlug = providerName.toLowerCase().replace(/[^a-z0-9]+/g, '-')

    return (
        <div className="openai-spend-page">
            <Wrapper
                title={title}
                description={description}
                actions={(
                    <>
                        <label className="openai-spend-period-control">
                            <span>Period</span>
                            <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
                                {PERIOD_OPTIONS.map((option) => (
                                    <option key={option} value={option}>{option} days</option>
                                ))}
                            </select>
                        </label>
                        <AppButton
                            icon="pi pi-refresh"
                            variant="info"
                            label={loading ? 'Refreshing...' : 'Refresh'}
                            onClick={loadDashboard}
                            disabled={loading}
                        />
                    </>
                )}
            >
                {loading && !dashboard ? (
                    <AdminNote tone="neutral">Loading {providerName} spend...</AdminNote>
                ) : null}
                {error ? (
                    <AdminNote tone="warning" title="Dashboard Error">{error}</AdminNote>
                ) : null}
                {dashboard && costs.available === false ? (
                    <AdminNote tone={costs.configured ? 'warning' : 'info'} title={costs.configured ? `${providerName} Costs Unavailable` : `${providerName} Costs Not Configured`}>
                        {costs.warning || `Set ${unconfiguredKeyName} to load billed spend from ${providerName}. Local intake activity is still shown.`}
                    </AdminNote>
                ) : null}
                {dashboard && usage && usage.available === false ? (
                    <AdminNote tone={usage.configured ? 'warning' : 'info'} title={usage.configured ? `${providerName} Usage Unavailable` : `${providerName} Usage Not Configured`}>
                        {usage.warning || `Set ${unconfiguredKeyName} to load usage from ${providerName}.`}
                    </AdminNote>
                ) : null}

                <dl className="openai-spend-metric-grid" aria-label={`${providerName} spend summary`}>
                    <MetricCard
                        label="Billed Spend"
                        value={costs.available ? formatMoney(costs.total_amount, currency) : 'Unavailable'}
                        detail={dashboard ? `${dashboard.period?.days || days} day period` : 'Waiting for data'}
                        tone="money"
                    />
                    <MetricCard
                        label="Local Calls"
                        value={formatInteger(localActivity.total_local_calls)}
                        detail={`${formatInteger(localActivity.completed_calls)} completed`}
                        tone="calls"
                    />
                    <MetricCard
                        label="Agent Runs"
                        value={formatInteger(localActivity.agent_runs)}
                        detail={`${formatInteger(localActivity.failed_calls)} failed`}
                        tone="agents"
                    />
                    <MetricCard
                        label="Research Tasks"
                        value={formatInteger(localActivity.research_tasks)}
                        detail={`${formatInteger(localActivity.running_calls)} queued or running`}
                        tone="research"
                    />
                    {localActivity.document_ai_runs !== undefined ? (
                        <MetricCard
                            label="Document Edits"
                            value={formatInteger(localActivity.document_ai_runs)}
                            detail="AI edit proposals"
                            tone="agents"
                        />
                    ) : null}
                    <MetricCard
                        label="Tracked Tokens"
                        value={formatInteger(localActivity.total_tokens)}
                        detail="From local agent run rows"
                        tone="tokens"
                    />
                    <MetricCard
                        label="DB Cost Estimate"
                        value={formatMoney(localActivity.estimated_cost, 'usd')}
                        detail={localActivity.priced_calls === undefined ? "Only priced workflow runs" : `${formatInteger(localActivity.priced_calls)} of ${formatInteger(localActivity.total_local_calls)} runs priced`}
                        tone="estimate"
                    />
                    {usage ? (
                        <MetricCard
                            label="API Tokens"
                            value={usage.available ? formatInteger(usage.total_tokens) : 'Unavailable'}
                            detail={`Official ${providerName} usage report`}
                            tone="tokens"
                        />
                    ) : null}
                    {usage ? (
                        <MetricCard
                            label="API Output"
                            value={usage.available ? formatInteger(usage.output_tokens) : 'Unavailable'}
                            detail={`${formatInteger(usage.input_tokens)} input tokens`}
                            tone="research"
                        />
                    ) : null}
                </dl>

                <SpendSection
                    id={`${providerSlug}-daily-spend`}
                    title="Daily Spend"
                    description={dailyDescription}
                    variant="focused"
                >
                    {dailyRows.length ? (
                        <div className="openai-spend-table-wrap">
                            <table className="openai-spend-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Billed Spend</th>
                                        <th>Spend Mix</th>
                                        <th>Local Calls</th>
                                        <th>Agent Runs</th>
                                        <th>Research</th>
                                        {localActivity.document_ai_runs !== undefined ? <th>Document Edits</th> : null}
                                        <th>Tracked Tokens</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dailyRows.map((row) => (
                                        <tr key={row.date}>
                                            <td>{formatDate(row.date)}</td>
                                            <td>{costs.available ? formatMoney(row.billed_amount, currency) : 'Unavailable'}</td>
                                            <td>
                                                <div className="openai-spend-bar-track" aria-hidden="true">
                                                    <div
                                                        className="openai-spend-bar-fill"
                                                        style={{ width: maxDailyAmount ? `${Math.max(3, (Number(row.billed_amount || 0) / maxDailyAmount) * 100)}%` : '0%' }}
                                                    />
                                                </div>
                                            </td>
                                            <td>{formatInteger(row.local_calls)}</td>
                                            <td>{formatInteger(row.agent_runs)}</td>
                                            <td>{formatInteger(row.research_tasks)}</td>
                                            {localActivity.document_ai_runs !== undefined ? <td>{formatInteger(row.document_ai_runs)}</td> : null}
                                            <td>{formatInteger(row.total_tokens)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <AdminEmptyState title={`No ${providerName} spend data`} description="No billed costs or local intake calls were found for this period." />
                    )}
                </SpendSection>

                <ProviderSpendDetails dashboard={dashboard || {}} providerName={providerName}
                    lineItemsDescription={lineItemsDescription} sourceDescription={sourceDescription} />
            </Wrapper>
        </div>
    )
}

/**
 * Render a caller-supplied billing report without fetching or owning filters. Keep provider invoice amounts distinct from local attempt estimates, and preserve unknown/unavailable report fields. The caller owns report freshness and selection.
 */
export function ProviderSpendDetails({ dashboard = {}, providerName, lineItemsDescription, sourceDescription }) {
    const costs = dashboard.costs || {}
    const usage = dashboard.usage || null
    const localActivity = dashboard.local_activity || {}
    const currency = costs.currency || 'usd'
    const providerSlug = providerName.toLowerCase().replace(/[^a-z0-9]+/g, '-')
    return <>
                <div className="openai-spend-grid-two">
                    <SpendSection
                        id={`${providerSlug}-cost-line-items`}
                        title="Cost Line Items"
                        description={lineItemsDescription}
                    >
                        <SimpleTable
                            emptyTitle="No line items"
                            columns={[
                                ['Line Item', (row) => row.line_item],
                                ['Amount', (row) => formatMoney(row.amount, row.currency || currency)],
                            ]}
                            rows={costs.line_items || []}
                        />
                    </SpendSection>
                    <SpendSection
                        id={`${providerSlug}-model-attribution`}
                        title="Model Attribution"
                        description="Tracked workflow runs grouped by model."
                    >
                        <SimpleTable
                            emptyTitle="No local model activity"
                            columns={[
                                ['Model', (row) => row.model],
                                ['Workflow runs', (row) => formatInteger(row.total_calls)],
                                ['Agents', (row) => formatInteger(row.agent_runs)],
                                ['Research', (row) => formatInteger(row.research_tasks)],
                                ...(localActivity.document_ai_runs !== undefined ? [['Document Edits', (row) => formatInteger(row.document_ai_runs)]] : []),
                                ['Failed', (row) => formatInteger(row.failed_calls)],
                                ['Tokens', (row) => formatInteger(row.total_tokens)],
                            ]}
                            rows={localActivity.by_model || []}
                        />
                    </SpendSection>
                </div>

                {usage ? (
                    <SpendSection
                        id={`${providerSlug}-provider-usage`}
                        title={`${providerName} Usage`}
                        description={`Official ${providerName} usage report grouped by model.`}
                    >
                        <SimpleTable
                            emptyTitle={`No ${providerName} usage data`}
                            columns={[
                                ['Model', (row) => row.model],
                                ['Input Tokens', (row) => formatInteger(row.input_tokens)],
                                ['Output Tokens', (row) => formatInteger(row.output_tokens)],
                                ['Cache Writes', (row) => formatInteger(row.cache_creation_input_tokens)],
                                ['Cache Reads', (row) => formatInteger(row.cache_read_input_tokens)],
                                ['Web Searches', (row) => formatInteger(row.web_search_requests)],
                            ]}
                            rows={usage.by_model || []}
                        />
                    </SpendSection>
                ) : null}

                <SpendSection
                    id={`${providerSlug}-local-sources`}
                    title="Local Sources"
                    description={sourceDescription}
                >
                    <SimpleTable
                        emptyTitle="No source activity"
                        columns={[
                            ['Source', (row) => row.source],
                            ['Type', (row) => row.kind],
                            ['Workflow runs', (row) => formatInteger(row.total_calls)],
                            ['Completed', (row) => formatInteger(row.completed_calls)],
                            ['Running', (row) => formatInteger(row.running_calls)],
                            ['Failed', (row) => formatInteger(row.failed_calls)],
                        ]}
                        rows={localActivity.by_source || []}
                    />
                </SpendSection>

                <SpendSection
                    id={`${providerSlug}-recent-local-calls`}
                    title="Recent workflow runs"
                    description={`Most recent locally tracked ${providerName} workflow runs.`}
                >
                    <SimpleTable
                        emptyTitle="No recent local calls"
                        className="openai-spend-recent-table"
                        columns={[
                            ['Created', (row) => formatDateTime(row.created_at)],
                            ['Type', (row) => row.kind],
                            ['Workflow', (row) => <span className="openai-spend-call-label">{row.label}</span>],
                            ['Model', (row) => row.model],
                            ['Status', (row) => <StatusBadge status={row.status} />],
                            ['Tokens', (row) => formatInteger(row.total_tokens)],
                            ['Estimated cost', (row) => formatMoney(row.estimated_cost, 'usd')],
                        ]}
                        rows={localActivity.recent_calls || []}
                    />
                </SpendSection>
    </>
}



function MetricCard({ label, value, detail, tone }) {
    return (
        <div className={`openai-spend-metric openai-spend-metric-${tone}`}>
            <dt className="openai-spend-metric-label">{label}</dt>
            <dd className="openai-spend-metric-value">{value}</dd>
            <dd className="openai-spend-metric-detail">{detail}</dd>
        </div>
    )
}



function SpendSection({ id, title, description, children, variant = 'section' }) {
    const headingId = `${id}-heading`
    return (
        <section
            className={`openai-spend-report openai-spend-report-${variant}`}
            aria-labelledby={headingId}
        >
            <header className="openai-spend-report-header">
                <h3 id={headingId}>{title}</h3>
                {description ? <p>{description}</p> : null}
            </header>
            <div className="openai-spend-report-body">{children}</div>
        </section>
    )
}



function SimpleTable({ columns, rows, emptyTitle, className = '' }) {
    if (!rows.length) {
        return <AdminEmptyState title={emptyTitle} />
    }

    return (
        <div className="openai-spend-table-wrap">
            <table className={`openai-spend-table ${className}`}>
                <thead>
                    <tr>
                        {columns.map(([label]) => <th key={label}>{label}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIndex) => (
                        <tr key={row.id ? `${row.kind || 'row'}-${row.id}` : (row.source || row.model || row.line_item || row.date || rowIndex)}>
                            {columns.map(([label, render]) => (
                                <td key={label}>{render(row)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}



function StatusBadge({ status }) {
    const normalized = `${status || 'unknown'}`.toLowerCase()
    return <span className={`openai-spend-status openai-spend-status-${normalized}`}>{status || 'unknown'}</span>
}



function mergeDailyRows(costRows, localRows) {
    const byDate = {}
    for (const row of costRows) {
        if (!row?.date) continue
        byDate[row.date] = {
            date: row.date,
            billed_amount: Number(row.amount || 0),
            local_calls: 0,
            agent_runs: 0,
            research_tasks: 0,
            document_ai_runs: 0,
            total_tokens: 0,
        }
    }
    for (const row of localRows) {
        if (!row?.date) continue
        byDate[row.date] = {
            ...(byDate[row.date] || { date: row.date, billed_amount: 0 }),
            local_calls: Number(row.local_calls || 0),
            agent_runs: Number(row.agent_runs || 0),
            research_tasks: Number(row.research_tasks || 0),
            document_ai_runs: Number(row.document_ai_runs || 0),
            total_tokens: Number(row.total_tokens || 0),
        }
    }
    return Object.values(byDate).sort((left, right) => `${left.date}`.localeCompare(`${right.date}`))
}



function formatMoney(value, currency = 'usd') {
    if (value === null || value === undefined) return 'Unknown'
    const amount = Number(value)
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: `${currency || 'usd'}`.toUpperCase(),
            minimumFractionDigits: amount > 0 && amount < 1 ? 4 : 2,
            maximumFractionDigits: amount > 0 && amount < 1 ? 4 : 2,
        }).format(amount)
    } catch (_error) {
        return `$${amount.toFixed(2)}`
    }
}



function formatInteger(value) {
    return new Intl.NumberFormat('en-US').format(Number(value || 0))
}



function formatDate(value) {
    if (!value) return 'Unknown'
    const date = new Date(`${value}T00:00:00Z`)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}



function formatDateTime(value) {
    if (!value) return 'Unknown'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return 'Unknown'
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default ProviderSpendDashboard
